import { DRAGGABLE_TYPES, Frames, OptionItem, OVERLAY_KIND, SEGMENT_KIND } from '@/redux/slices/packagingSlice.types';
import { nanoid } from 'nanoid';

export const DRAGGABLE_TYPES_ALLOWED_MEDIA = {
  [DRAGGABLE_TYPES.SEGMENT]: ['video'],
  [DRAGGABLE_TYPES.OVERLAY]: ['image', 'video']
};
export const EMPTY_TIMELINE_DEFAULT_DURATION = 15 * 60_000;

export const DEFAULT_OVERLAY_DURATION: Frames = 3000;
export const DEFAULT_SEGMENT_DURATION: Frames = 3000;
export const DEFAULT_BODY_SEGMENT_DURATION: Frames = 53946; // 30 min = 1800 seconds * 29.97 fps = 53946 frames

// Defines how many milliseconds per pixel are displayed with zoom level equal to 1
export const PIXELS_PER_MINUTE = 128; // how many pixels on screen one minute take with scale equal 1
export const MILLISECONDS_PER_PIXEL = 60_000 / PIXELS_PER_MINUTE; // one minute per 128 pixels

export const SEGMENTS_GAP = 4;
export const RESIZE_HANDLE_WIDTH = 12;

export const TIMELINE_MIN_SCALE = 0.05;
export const TIMELINE_MAX_SCALE = 100;

export const TIMELINE_SIDES_PADDING = 40;
export const SEGMENT_CONTAINER_PADDING = 5;

export const SEGMENT_HEIGHT = 85;

// Player
export const PLAYER_TOP_PANEL_CLASSNAME = 'player-top-panel';
export const PLAYER_CONTROLS_CLASSNAME = 'player-controls';

// STYLES

// z-indexes
export const PLAY_HEAD_VERTICAL_LINE_ZINDEX = 3;
export const SEGMENT_TOOLBAR_POPPER_ZINDEX = PLAY_HEAD_VERTICAL_LINE_ZINDEX + 1;
const ACCURATE_PLAYERS_ZINDEX = 1; // this values used internally in the accurate player
export const OVERLAYS_PREVIEW_ZINDEX = ACCURATE_PLAYERS_ZINDEX + 1;
export const PLAYER_TOP_PANEL_ZINDEX = ACCURATE_PLAYERS_ZINDEX + 2;
export const OVERLAYS_PREVIEW_LABELS_ZINDEX = ACCURATE_PLAYERS_ZINDEX + 2;
export const NO_ASSIGNED_VIDEO_WARNING_ZINDEX = ACCURATE_PLAYERS_ZINDEX + 3;
export const PLAYER_LOADER_ZINDEX = ACCURATE_PLAYERS_ZINDEX + 4;
export const PLAYER_CONTROLS_ZINDEX = ACCURATE_PLAYERS_ZINDEX + 100; // should be on top of everything else

// Colors
export const UNASSIGNED_SEGMENT_BORDER_COLOR_DARK_THEME = 'rgba(255, 255, 255, 0.3)';
export const UNASSIGNED_SEGMENT_BORDER_COLOR_LIGHT_THEME = 'rgba(0, 0, 0, 0.4)';
export const UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_DARK_THEME = 'rgba(255, 255, 255, 0.2)';
export const UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_LIGHT_THEME = 'rgba(0, 0, 0, 0.2)';
export const UNASSIGNED_SEGMENT_GRIP_COLOR_DARK_THEME = 'rgba(255, 255, 255, 0.6)';
export const UNASSIGNED_SEGMENT_GRIP_COLOR_LIGHT_THEME = 'rgba(0, 0, 0, 0.6)';
export const UNASSIGNED_OVERLAY_COLOR_DARK_THEME = 'rgba(255, 255, 255, 0.5)';
export const UNASSIGNED_OVERLAY_COLOR_LIGHT_THEME = 'rgba(0, 0, 0, 0.6)';

export enum FILE_LABEL {
  SM = 'servicing_master',
  SMP = 'servicing_master_proxy',
  SMP2997 = 'servicing_master_proxy_2997',
  SI = 'servicing_image'
}

export const MASTER_VIDEO_LABELS = [FILE_LABEL.SM] as const;
export const PROXY_VIDEO_LABELS = [FILE_LABEL.SMP, FILE_LABEL.SMP2997] as const;
export const IMAGE_LABELS = [FILE_LABEL.SI] as const;

export const BODY_SEGMENT_KINDS = [SEGMENT_KIND.BODY, SEGMENT_KIND.BODY_WITH_CREDITS];

// Derives all SEGMENT_KIND values whose enum key starts with "CREDITS" — automatically includes any future CREDITS_* additions
type CreditsKind = {
  [K in keyof typeof SEGMENT_KIND]: K extends `CREDITS${string}` ? (typeof SEGMENT_KIND)[K] : never;
}[keyof typeof SEGMENT_KIND];

export type OpenTeaseOrCreditsKind = SEGMENT_KIND.OPEN_TEASE | CreditsKind;

export const OPEN_TEASE_AND_CREDITS_KINDS: OpenTeaseOrCreditsKind[] = Object.entries(SEGMENT_KIND)
  .filter(([key]) => key === 'OPEN_TEASE' || key.startsWith('CREDITS'))
  .map(([, value]) => value as OpenTeaseOrCreditsKind);

export const SEGMENT_KINDS_TO_PRESERVE: SEGMENT_KIND[] = [SEGMENT_KIND.BODY, SEGMENT_KIND.BODY_WITH_CREDITS, ...OPEN_TEASE_AND_CREDITS_KINDS];

// That map shows which master label maps to which proxy label so we could pick corresponding proxy file for a given master file
export const MASTER_TO_PROXY_MAP = {
  [FILE_LABEL.SM]: FILE_LABEL.SMP2997
};

const BODY_OPTION_BASE = { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.BODY, label: 'Body' } as const;
const BODY_WITH_CREDITS_OPTION_BASE = {
  segmentType: DRAGGABLE_TYPES.SEGMENT,
  kind: SEGMENT_KIND.BODY_WITH_CREDITS,
  label: 'Body (with Credits)'
} as const;
const OPEN_TEASE_OPTION_BASE = { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.OPEN_TEASE, label: 'Open/Tease' };
const CREDITS_OPTION_BASE = {
  segmentType: DRAGGABLE_TYPES.SEGMENT,
  kind: SEGMENT_KIND.CREDITS,
  label: 'Credits'
};

// Base objects for packaging segments and overlays
const OPTIONS_LIST = [
  BODY_OPTION_BASE,
  BODY_WITH_CREDITS_OPTION_BASE,
  OPEN_TEASE_OPTION_BASE,
  CREDITS_OPTION_BASE,
  {
    segmentType: DRAGGABLE_TYPES.OVERLAY,
    kind: OVERLAY_KIND.RATING_CARD,
    label: 'Rating Card'
  },
  { segmentType: DRAGGABLE_TYPES.OVERLAY, kind: OVERLAY_KIND.HASHTAG, label: 'Hashtag' },
  {
    segmentType: DRAGGABLE_TYPES.OVERLAY,
    kind: OVERLAY_KIND.LOWER_THIRD,
    label: 'Lower Third'
  },
  {
    segmentType: DRAGGABLE_TYPES.OVERLAY,
    kind: OVERLAY_KIND.BUG,
    label: 'Bug'
  },
  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.PROMO,
    label: 'Promo'
  },
  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.BRAND_ID,
    label: 'Brand ID'
  },
  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.UNDERWRITING,
    label: 'Underwriting'
  },

  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.CREDITS_WITH_WEBTAG_AND_OFFER,
    label: 'Credits with Webtag and Offer'
  },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.CREDITS_WITH_WEBTAG, label: 'Credits with Webtag' },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.CREDITS_WITH_OFFER, label: 'Credits with Offer' },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.WEBTAG, label: 'Webtag' },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.OFFER, label: 'Offer' },
  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.SYSTEM_CUE,
    label: 'System Cue'
  },
  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.CONTENT_FILLER,
    label: 'Content Filler'
  },
  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.FILLER,
    label: 'Filler'
  },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.PLEDGE_BREAK, label: 'Pledge Break' },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.BLACK, label: 'Black' },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.ADVISORY, label: 'Advisory' },
  { segmentType: DRAGGABLE_TYPES.SEGMENT, kind: SEGMENT_KIND.NEXT_TIME_TEASE, label: 'Next Time Tease' },
  {
    segmentType: DRAGGABLE_TYPES.SEGMENT,
    kind: SEGMENT_KIND.NEXT_TIME_TEASE_WITH_CREDITS,
    label: 'Next Time Tease (with Credits)'
  }
].map((item): OptionItem => {
  if (item.segmentType === DRAGGABLE_TYPES.SEGMENT) {
    return {
      ...item,
      draggableId: nanoid(),
      type: DRAGGABLE_TYPES.OPTION,
      segmentType: DRAGGABLE_TYPES.SEGMENT,
      kind: item.kind as SEGMENT_KIND,
      isLocked: false
    };
  }

  return {
    ...item,
    draggableId: nanoid(),
    type: DRAGGABLE_TYPES.OPTION,
    segmentType: DRAGGABLE_TYPES.OVERLAY,
    kind: item.kind as OVERLAY_KIND,
    isLocked: false
  };
});

export const PACKAGING_TOOLBOX_OPTIONS_LIST = OPTIONS_LIST.filter((option) => {
  const filteredOutOptionKinds: (SEGMENT_KIND | OVERLAY_KIND)[] = BODY_SEGMENT_KINDS;
  return !filteredOutOptionKinds.includes(option.kind);
});

export const EDIT_TEMPLATE_TOOLBOX_OPTIONS_LIST = OPTIONS_LIST;

export const OPTIONALLY_ASSIGNED_SEGMENT_KINDS = [...BODY_SEGMENT_KINDS, SEGMENT_KIND.OPEN_TEASE, SEGMENT_KIND.CREDITS];
export const OPTIONALLY_ASSIGNED_SEGMENT_KINDS_LABELS: Record<string, string> = {
  [SEGMENT_KIND.BODY]: BODY_OPTION_BASE.label,
  [SEGMENT_KIND.BODY_WITH_CREDITS]: BODY_WITH_CREDITS_OPTION_BASE.label,
  [SEGMENT_KIND.OPEN_TEASE]: OPEN_TEASE_OPTION_BASE.label,
  [SEGMENT_KIND.CREDITS]: CREDITS_OPTION_BASE.label
};
